/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2022-2023 (original work) Open Assessment Technologies SA
 *
 */
import { log } from '@/core/utils';
import { writable, get } from 'svelte/store';
import { getUser } from '@/modules/users/dataServices/userService';
import { getUser as getUserTokenData } from '@/services/authService';
import { initialize } from '@/services/analyticsService';

export const user = createAuthStore();

function createAuthStore() {
    const userStore = writable({});
    const { subscribe, set, update } = userStore;

    return {
        get() {
            return get(user);
        },
        subscribe,
        set,
        update,
        async updateInfo() {
            await this.updateTokenData();
            const currentUser = get(user);
            const userId = currentUser?.userId;
            if (!userId) {
                return;
            }
            const {
                active,
                email,
                emailVerified,
                login,
                name,
                language,
                features = [],
                _id
            } = await getUser(userId);
            update(s => ({
                ...s,
                active,
                email,
                emailVerified,
                login,
                name,
                language,
                features,
                id: _id
            }));
            initialize();
        },
        async updateTokenData() {
            try {
                const { userId, roles, hierarchy } = await getUserTokenData();
                update(s => ({
                    ...s,
                    userId,
                    roles,
                    hierarchy
                }));
            } catch (err) {
                log.warn("Couldn't update token data:", err.message);
                set(null);
            }
        },
        /**
         * Get hierarchyId and organizationId from user store
         * @returns {{hierarchyId: string, organizationId: string}}
         */
        getHierarchy() {
            const hierarchy = get(user)?.hierarchy ?? {};
            const hierarchyId = Object.keys(hierarchy)[0];
            const organizationId = hierarchy[hierarchyId];
            return {
                hierarchyId,
                organizationId
            };
        }
    };
}
